import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { animate, useReducedMotion, type AnimationPlaybackControls } from 'motion/react';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import checkIconUrl from '../../assets/Check.svg';
import { UnitCompletedPill } from './UnitCompletedPill';

// A segment the student can jump to, with the short label shown on the arrow link.
interface AdjacentSegment {
  label: string;
  onSelect: () => void;
}

interface Props {
  // Short label of the current segment: "Foundations" | "Target N" (accent-colored).
  label: string;
  // Target/checkpoint name shown next to the ordinal (e.g. "Equation"); empty for Foundations.
  name: string;
  // Completed / total skills in the current segment (drives the bar fill and the student marker).
  completed: number;
  total: number;
  // Unit accent color (hex) for the ordinal and the progress-bar value fill.
  color: string;
  // Current student, for the position marker on the bar.
  studentInitials: string;
  studentPicture?: string;
  // Adjacent segments — the left arrow is hidden when there is nowhere to move back to; with no next
  // segment the right slot shows the unlit end-of-unit pill instead of an arrow.
  previous?: AdjacentSegment;
  next?: AdjacentSegment;
  // Scrolls the student back to their current position in the path (same as BackToPositionButton).
  // Shown as a button below the bar when the segment has no progress or is complete (no avatar marker).
  onBackToPosition: () => void;
  // Whether their current position is off screen. False hides the button even with no avatar marker:
  // the rail's "Back to position" control is hidden in that case too (the position is right there), so
  // offering the scroll here would contradict it.
  isPositionOutOfView: boolean;
}

const navLinkClassName =
  "tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-[#357AF6] tw-underline tw-underline-offset-4 tw-font-['Atkinson_Hyperlegible']";

// Matches PrimeReact's default progress-bar value transition, so the fill and the student marker glide
// together when driven from the same animated value.
const FILL_DURATION = 1;

// Both slots flanking the bar keep a fixed height. The right one holds either a text link (~20px) or
// the compact UnitCompletedPill (~30px at this width), so letting the row follow whichever is in it
// would tie the header's height to the active segment — see the back-to-position slot below for why
// that is not allowed here.
const NAV_SLOT_CLASS_NAME = 'tw-flex tw-h-8 tw-w-28 tw-shrink-0 tw-items-center';

// The "Working towards" indicator for the unit-path segment the student is currently scrolled to: the
// current target's name plus a progress bar with the student's avatar marking their position, flanked
// by links that jump to the adjacent segments. The bar value and the avatar position are driven by one
// animated number so they move in lockstep (on mount and on segment change). Only the progress-bar
// value is unit-colored (via the `pt` passthrough); the track keeps the default background.
export const UnitPathSegmentIndicator: FC<Props> = ({
  label,
  name,
  completed,
  total,
  color,
  studentInitials,
  studentPicture,
  previous,
  next,
  onBackToPosition,
  isPositionOutOfView,
}) => {
  const targetPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComplete = total > 0 && completed >= total;
  // The student marker only rides the bar while the segment is partially done; with no progress or
  // once complete we offer a "Back to my position" button instead — but only while that position is
  // actually off screen, so this button and the rail's appear and disappear together.
  const showsAvatar = completed > 0 && completed < total;
  const showsBackToPosition = !showsAvatar && isPositionOutOfView;
  const shouldReduceMotion = useReducedMotion();
  // Starts at 0 so the fill (and the marker) animate in from the left on mount, then eases to the
  // target and to each new segment's value.
  const [displayPercent, setDisplayPercent] = useState<number>(0);
  const displayPercentRef = useRef<number>(0);
  displayPercentRef.current = displayPercent;

  useEffect(() => {
    if (shouldReduceMotion) {
      setDisplayPercent(targetPercent);
      return;
    }
    const controls: AnimationPlaybackControls = animate(displayPercentRef.current, targetPercent, {
      duration: FILL_DURATION,
      ease: 'easeInOut',
      onUpdate: value => setDisplayPercent(value),
    });
    return () => controls.stop();
  }, [targetPercent, shouldReduceMotion]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-font-['Atkinson_Hyperlegible']" aria-live="polite">
      <div className="tw-text-center">
        {/* Fixed one-line height (tw-h-4 == the text-xs line box and the check icon): with no caption
            at all this line would collapse to 0 and change the header's height, which — see the slot
            comment below — reflows the path the scroll-spy measures. */}
        <div className="tw-flex tw-h-4 tw-items-center tw-justify-center tw-gap-1 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-500">
          {isComplete ? (
            <>
              Done
              <img src={checkIconUrl} alt="" aria-hidden="true" className="tw-h-4 tw-w-4" />
            </>
          ) : completed > 0 ? (
            'Working towards'
          ) : (
            ''
          )}
        </div>
        <div className="tw-text-lg tw-font-bold">
          <span style={{ color: completed > 0 ? color : '' }} className="tw-text-neutral-600">
            {label}
          </span>
          {name && <span className={completed > 0 ? 'tw-text-neutral-950' : 'tw-text-neutral-600'}> - {name}</span>}
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-gap-4">
        <div className={NAV_SLOT_CLASS_NAME}>
          {previous && (
            <button
              type="button"
              onClick={previous.onSelect}
              className={navLinkClassName}
              aria-label={`Go to ${previous.label}`}
            >
              <ChevronLeft className="tw-h-4 tw-w-4" aria-hidden="true" />
              {previous.label}
            </button>
          )}
        </div>

        <div className="tw-relative tw-flex-1 tw-mt-3">
          <ProgressBar
            value={displayPercent}
            showValue={false}
            className="tw-overflow-hidden tw-rounded-full"
            style={{ height: '0.75rem' }}
            // We drive the width from `displayPercent` every frame, so disable PrimeReact's own
            // width transition to avoid double-animating it out of sync with the marker.
            pt={{ value: { style: { background: color, transition: 'none' } } }}
            aria-label={`${label} progress`}
          />
          {showsAvatar && (
            <div
              className="tw-absolute tw-top-1/2"
              style={{ left: `${displayPercent}%`, transform: 'translate(-50%, calc(-50% - 11px))' }}
            >
              <Tooltip target=".nc_unit-path-marker" position="top" className="nc_unit-path-marker-tooltip">
                <span className="tw-font-bold">{completed}</span>/{total} skills done!
              </Tooltip>
              <Avatar
                image={studentPicture}
                label={studentInitials || undefined}
                shape="circle"
                className="nc_unit-path-marker tw-h-9 tw-w-9 tw-border-2 tw-border-solid tw-border-white tw-bg-[#A78BFA] tw-text-sm tw-font-bold tw-text-white tw-shadow"
              />
            </div>
          )}
        </div>

        <div className={`${NAV_SLOT_CLASS_NAME} tw-justify-end`}>
          {next ? (
            <button
              type="button"
              onClick={next.onSelect}
              className={navLinkClassName}
              aria-label={`Go to ${next.label}`}
            >
              {next.label}
              <ChevronRight className="tw-h-4 tw-w-4" aria-hidden="true" />
            </button>
          ) : (
            // Last segment: there is nowhere left to jump to, so the slot previews the unit's
            // end-of-path banner — always in its unlit (locked) look, since reaching the end of the
            // last segment is not the same as having completed the unit.
            <UnitCompletedPill tinted={false} compact />
          )}
        </div>
      </div>

      {/* The slot keeps its height whether or not the button is in it. The header is sticky, so it
          still takes up space in the flow: any height change in here reflows the whole unit path
          below and moves the very segment anchors useActiveUnitPathSegment measures — which flips the
          active segment, re-renders this block, and oscillates. tw-h-9 is the button's own height
          (py-2 + the text-sm line box). */}
      <div className="tw-flex tw-h-9 tw-items-center tw-justify-center">
        {showsBackToPosition && (
          <button
            type="button"
            onClick={onBackToPosition}
            className="tw-inline-flex tw-cursor-pointer tw-items-center tw-rounded-full tw-border-none tw-bg-[#357AF6] tw-px-5 tw-py-2 tw-text-sm tw-font-bold tw-text-white tw-shadow-sm hover:tw-bg-[#2f6fed] tw-font-['Atkinson_Hyperlegible']"
          >
            Back to my position
          </button>
        )}
      </div>
    </div>
  );
};
